import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getSearchParam } from 'helpers/getSearchParam';

import {
  AccountManager,
  btag,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  pushToDataLayer,
  SiteConfigManager,
  StorageManager,
  useApplicationState,
  useCasinoNewState,
  useSettings,
} from '@atlas/core';
import { EmptyStateView, useViewportObserverContext } from '@atlas/ui';

import { CasinoBanner } from 'components/Pages/Casino/CasinoBanner';
import CasinoNavigation from 'components/Pages/Casino/CasinoNavigation';
import { GROUPS } from 'components/Pages/Casino/constants';
import GameListSwitch from 'components/Pages/Casino/GameList/GameListSwitch';
import { casinoFilterTypeMap, getFilter } from 'components/Pages/Casino/useCasinoFilter';
import SeoPageText from 'components/SeoPage/SeoPageText';
import QuickDepositPopup from 'src/common/Popup/QuickDepositPopup/QuickDepositPopup';
import PaymentIframe from 'src/components/ClientProfile/Payment/Deposit/PaymentIframe';
import MultiFilterSelectedList from 'src/components/Pages/Casino/Filter/MultiFilter/MultiFilterSelectedList';
import LastWinners from 'src/components/Pages/Casino/LastWinners/LastWinners';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import CasinoLobby from './CasinoLobby/CasinoLobby';
import Filter from './Filter/Filter';
import { useCasinoFilterState } from './CasinoFilterContext';

const quickDepositGroups = {
  [GROUPS.CASINO]: 'casino_page',
  [GROUPS.LIVE]: 'live_casino_page',
};

const BALANCE_MULTIPLIER = 100;
const MAX_DELAY_COUNT = 3;
export const QUICK_DEPOSIT_DELAY = 'quick_deposit_delay';

const MainCasinoPage = () => {
  const { push: historyPush } = useHistory();
  const { state } = useLocation();
  const { accountBlocked, redirectAfterPopup, selfRegulationPopupShow, showRealityCheckPopup } =
    state || {};

  const {
    categories: { error: categoriesError },
    group,
    providers: { error: providersError },
  } = useCasinoNewState();

  const { filter, providerFilterActive, updateFilter } = useCasinoFilterState();

  const { account, authenticated, bankAccounts, wallet } = useApplicationState();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const showSpentLimitPopupIfNeededRef = useRef(showSpentLimitPopupIfNeeded);
  showSpentLimitPopupIfNeededRef.current = showSpentLimitPopupIfNeeded;
  const showSelfRegulationPopupIfNeededRef = useRef(showSelfRegulationPopupIfNeeded);
  showSelfRegulationPopupIfNeededRef.current = showSelfRegulationPopupIfNeeded;
  const { BONUS = 0, CASH = 0, FROZEN = 0 } = wallet.detailedBalance || {};
  const balance = BONUS + CASH + FROZEN;
  const { status } = account;

  const searchCategory = getSearchParam(window.location.search, 'category');
  const searchProviderNames = getSearchParam(window.location.search, 'providerNames');
  const searchQuery = getFilter(casinoFilterTypeMap.name) || '';
  const viewportObserverRef = useViewportObserverContext();
  const scrollParent = useCallback(() => viewportObserverRef.current, [viewportObserverRef]);

  const resetPageToShowProviders = useCallback(() => {
    setActiveItem('all_games');
    updateFilter({ categories: [] });
    viewportObserverRef.current?.scrollTo(0, 0);
  }, [updateFilter, viewportObserverRef]);

  useEffect(() => {
    // filter by provider on mount when there is providerNames search param
    if (searchProviderNames?.split(',')?.length > 0) {
      resetPageToShowProviders();
    }
  }, [resetPageToShowProviders, searchProviderNames]);

  const [activeItem, setActiveItem] = useState();

  const [showFavorites, setShowFavorites] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(!!searchQuery);
  const [showQuickDepositPopup, setShowQuickDepositPopup] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState();
  const { lang } = useSettings();

  const resetAfterHoursTimeoutId = useRef(null);
  const showQuickDepositTimeoutId = useRef(null);

  const homePageFunctionality = SiteConfigManager.getConfig(
    'config.config.casino.mobile_home_page',
  );

  const quickDepositEnabled = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.enabled',
  );
  const quickDepositMinBalance = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.minBalance',
  );
  const resetAfterHours = SiteConfigManager.getConfig(
    'config.config.general.quickDepositPopup.reset_after_hours',
  );

  const quickDepositPages = useMemo(
    () => SiteConfigManager.getConfig('config.config.general.quickDepositPopup.pages'),
    [],
  );

  const closeQuickDepositPopup = useCallback(() => {
    setShowQuickDepositPopup(false);
  }, [setShowQuickDepositPopup]);

  const closeQuickDepositPopupWithSetDelay = useCallback(() => {
    setShowQuickDepositPopup(false);

    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);
    const { delay = 0 } = delayInfo ? JSON.parse(delayInfo) : {};
    const currentDelay = delay === MAX_DELAY_COUNT ? MAX_DELAY_COUNT : delay + 1;
    const delayAfterClose = SiteConfigManager.getConfig(
      `config.config.general.quickDepositPopup.delay_after_close_${currentDelay}`,
    );

    const currentDelayInfo = {
      delay: currentDelay,
      delayStartTime: Date.now(),
    };

    StorageManager.setItem(QUICK_DEPOSIT_DELAY, JSON.stringify(currentDelayInfo));

    showQuickDepositTimeoutId.current = setTimeout(() => {
      setShowQuickDepositPopup(true);
    }, delayAfterClose * 1000);

    if (resetAfterHoursTimeoutId.current) {
      clearTimeout(resetAfterHoursTimeoutId.current);
    }

    if (resetAfterHours) {
      const resetDelay = resetAfterHours * 3600 * 1000;

      resetAfterHoursTimeoutId.current = setTimeout(() => {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }, resetDelay);
    }
  }, [resetAfterHours]);

  const showQuickDepositPopupWithDelay = useCallback(delayInfo => {
    const { delay, delayStartTime } = JSON.parse(delayInfo);

    const delayAfterClose = SiteConfigManager.getConfig(
      `config.config.general.quickDepositPopup.delay_after_close_${delay}`,
    );
    const timeToShowPopup = delayStartTime + delayAfterClose * 1000;
    const currentDelay = Math.max(timeToShowPopup - Date.now(), 0);

    if (currentDelay > 0) {
      showQuickDepositTimeoutId.current = setTimeout(() => {
        setShowQuickDepositPopup(true);
      }, currentDelay);
    } else {
      setShowQuickDepositPopup(true);
    }
  }, []);

  const resetQuickDepositDelay = useCallback(() => {
    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);

    if (!delayInfo || !resetAfterHours) return;
    const { delayStartTime } = JSON.parse(delayInfo);
    const timeToResetDelay = delayStartTime + resetAfterHours * 3600 * 1000;
    const resetDelay = timeToResetDelay - Date.now();

    if (resetDelay > 0) {
      resetAfterHoursTimeoutId.current = setTimeout(() => {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }, resetDelay);
    } else {
      StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
    }
  }, [resetAfterHours]);

  const isCasinoLobbyConstructorEnabled = SiteConfigManager.getConfig(
    'config.config.casino.isCasinoLobbyConstructorEnabled',
    false,
  );
  const casinoLobbyConfig = SiteConfigManager.getConfig('casino_lobby_config', null);

  const shouldRenderLobby = useMemo(() => {
    const structure = casinoLobbyConfig?.lobbyStructure;
    return (
      isCasinoLobbyConstructorEnabled &&
      Array.isArray(structure) &&
      structure.length > 0 &&
      structure.every(block => block?.type) &&
      !isSearchActive &&
      !showFavorites &&
      homePageFunctionality &&
      activeItem === 'home'
    );
  }, [
    isCasinoLobbyConstructorEnabled,
    casinoLobbyConfig,
    isSearchActive,
    showFavorites,
    homePageFunctionality,
    activeItem,
  ]);

  useEffect(() => {
    const { clientId } = AccountManager.account;

    pushToDataLayer(
      'open-casino',
      {
        client: { id: clientId },
        language: lang,
      },
      btag,
    );
  }, []);

  useEffect(() => {
    if (
      !authenticated ||
      !quickDepositEnabled ||
      !quickDepositPages.includes(quickDepositGroups[group]) ||
      (needsVerification(account, bankAccounts) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.DEPOSIT))
    ) {
      return;
    }

    resetQuickDepositDelay();

    const delayInfo = StorageManager.getItem(QUICK_DEPOSIT_DELAY);

    if (balance < quickDepositMinBalance * BALANCE_MULTIPLIER) {
      if (delayInfo) {
        showQuickDepositPopupWithDelay(delayInfo);
      } else {
        setShowQuickDepositPopup(true);
      }
    } else {
      if (showQuickDepositTimeoutId.current) {
        clearTimeout(showQuickDepositTimeoutId.current);
      }

      if (delayInfo) {
        StorageManager.removeItem(QUICK_DEPOSIT_DELAY);
      }
    }
  }, [
    account,
    authenticated,
    balance,
    bankAccounts,
    group,
    quickDepositEnabled,
    quickDepositMinBalance,
    quickDepositPages,
    resetQuickDepositDelay,
    showQuickDepositPopupWithDelay,
  ]);

  useEffect(() => {
    const categoryFilter = filter.categories[0];

    if (
      (!searchCategory && !!categoryFilter !== !!searchCategory) ||
      (searchCategory && categoryFilter !== searchCategory)
    ) {
      const newCategoriesFilter = searchCategory ? [searchCategory] : [];

      updateFilter({ categories: newCategoriesFilter });
    }
  }, [searchCategory]);

  useEffect(() => {
    if (accountBlocked) {
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
    }

    if (selfRegulationPopupShow || showRealityCheckPopup) {
      (async () => {
        const spentLimitSuccess = await showSpentLimitPopupIfNeededRef.current();

        if (!spentLimitSuccess) return;

        const selfRegulationSuccess = await showSelfRegulationPopupIfNeededRef.current();

        if (redirectAfterPopup && selfRegulationSuccess) {
          historyPush(redirectAfterPopup);
        }
      })();
    }
  }, [
    accountBlocked,
    historyPush,
    redirectAfterPopup,
    selfRegulationPopupShow,
    showRealityCheckPopup,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(showQuickDepositTimeoutId.current);
      clearTimeout(resetAfterHoursTimeoutId.current);
    };
  }, []);

  if (categoriesError || providersError) {
    return (
      <EmptyStateView
        className="viewport-height"
        description="page_loading_error"
        title="page_loading_error_title"
      />
    );
  }

  const layoutToScrollClass = cx('layout-to-scroll', { hidden: providerFilterActive });
  return (
    <div className={layoutToScrollClass}>
      <div className="casino-home-page">
        <div className="casino-home-page-content">
          <CasinoBanner />
          {homePageFunctionality ? (
            <CasinoNavigation
              activeItem={activeItem}
              isSearchActive={isSearchActive}
              setActiveItem={setActiveItem}
              setIsSearchActive={setIsSearchActive}
              setShowFavorites={setShowFavorites}
              showFavorites={showFavorites}
            />
          ) : (
            <Filter
              isSearchActive={isSearchActive}
              setIsSearchActive={setIsSearchActive}
              setShowFavorites={setShowFavorites}
              showFavorites={showFavorites}
            />
          )}

          {!isSearchActive && <LastWinners isAuthenticated={authenticated} status={status} />}

          {!homePageFunctionality && <MultiFilterSelectedList />}

          {shouldRenderLobby ? (
            <CasinoLobby onProviderClick={resetPageToShowProviders} setActiveItem={setActiveItem} />
          ) : (
            <GameListSwitch
              activeItem={activeItem}
              homePageFunctionality={homePageFunctionality}
              isSearchActive={isSearchActive}
              scrollParent={scrollParent}
              searchQuery={searchQuery}
              setActiveItem={setActiveItem}
              showFavorites={showFavorites}
            />
          )}
        </div>
        <SeoPageText />

        {showQuickDepositPopup && authenticated && (
          <QuickDepositPopup
            onClose={closeQuickDepositPopup}
            onCloseClick={closeQuickDepositPopupWithSetDelay}
            setPaymentUrl={setPaymentUrl}
          />
        )}
        {paymentUrl && <PaymentIframe paymentUrl={paymentUrl} setPaymentUrl={setPaymentUrl} />}
      </div>
    </div>
  );
};

export default MainCasinoPage;
